/**
 * The theme document: one live theme plus any number of drafts.
 *
 * Shape:
 *   {
 *     liveId: string|null,
 *     themes: [{ id, name, status: 'live'|'draft'|'published', ...theme }]
 *   }
 *
 * Rules that the rest of the app relies on:
 * - Only the entry matching liveId is ever rendered to visitors.
 * - Saving a draft must not change liveId. Publishing is the only thing that does.
 * - A missing document is not an error: the public renderer falls back to the
 *   legacy adapter, so an untouched site keeps its current look.
 */

import { DEFAULT_THEME } from './defaults.js';
import { mergeTheme } from './legacy.js';
import { ensureHeaderBlocks } from './header-blocks.js';

export const THEME_STATUS = { LIVE: 'live', DRAFT: 'draft', PUBLISHED: 'published' };
export const MAX_THEMES = 8;
export const OVERRIDE_LAYERS = ['phone', 'tablet'];

/** Keys on a theme entry that are metadata rather than rendered values. */
const META_KEYS = ['id', 'name', 'status', 'createdAt', 'updatedAt'];

function randomId() {
    return `theme_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {Object} document
 * @returns {Object} A document that is always safe to read from
 */
export function normalizeThemeDocument(document) {
    const themes = Array.isArray(document?.themes) ? document.themes.slice(0, MAX_THEMES) : [];

    const normalized = themes
        .filter((entry) => entry && typeof entry === 'object')
        .map((entry, index) => ({
            ...entry,
            id: entry.id || randomId(),
            name: entry.name || `Theme ${index + 1}`,
            status: Object.values(THEME_STATUS).includes(entry.status) ? entry.status : THEME_STATUS.DRAFT,
        }));

    let liveId = document?.liveId || null;
    if (liveId && !normalized.some((entry) => entry.id === liveId)) liveId = null;

    return { liveId, themes: normalized };
}

/**
 * @param {Object} document
 * @param {string} id
 * @returns {Object|null}
 */
export function findTheme(document, id) {
    return normalizeThemeDocument(document).themes.find((entry) => entry.id === id) || null;
}

/**
 * Resolve a theme entry into a complete theme by layering it over a base.
 * @param {Object} entry - A theme document entry
 * @param {Object} base - Usually the legacy-compiled theme for this site
 * @returns {Object}
 */
export function resolveThemeEntry(entry, base = DEFAULT_THEME) {
    if (!entry) return ensureHeaderBlocks(base);
    return ensureHeaderBlocks(mergeTheme(base, entry));
}

/**
 * Create a draft from an existing theme, or from the given base when there is
 * nothing to copy yet. The draft is never live.
 *
 * @param {Object} document
 * @param {Object} options
 * @param {string} [options.fromId] - Theme to copy; defaults to the live one
 * @param {Object} [options.base] - Theme values to use when there is no source
 * @param {string} [options.name]
 * @returns {Object} { document, draft }
 */
export function createDraft(document, { fromId, base, name } = {}) {
    const normalized = normalizeThemeDocument(document);
    const sourceId = fromId || normalized.liveId;
    const source = sourceId ? normalized.themes.find((entry) => entry.id === sourceId) : null;

    const values = source
        ? JSON.parse(JSON.stringify(stripMeta(source)))
        : JSON.parse(JSON.stringify(stripMeta(base || DEFAULT_THEME)));

    const draft = {
        ...values,
        id: randomId(),
        name: name || nextDraftName(normalized, source?.name),
        status: THEME_STATUS.DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    };

    if (normalized.themes.length >= MAX_THEMES) {
        throw new Error(`A site can hold at most ${MAX_THEMES} themes. Delete one first.`);
    }

    return {
        document: { ...normalized, themes: [...normalized.themes, draft] },
        draft,
    };
}

function stripMeta(entry) {
    const out = { ...entry };
    META_KEYS.forEach((key) => delete out[key]);
    return out;
}

function nextDraftName(document, sourceName) {
    const taken = new Set(document.themes.map((entry) => entry.name));
    const stem = sourceName ? `${sourceName} copy` : 'Draft';
    if (!taken.has(stem)) return stem;

    let suffix = 2;
    while (taken.has(`${stem} ${suffix}`)) suffix += 1;
    return `${stem} ${suffix}`;
}

/**
 * Store new values on one theme. Refuses to touch liveId, so saving a draft can
 * never change what visitors see.
 *
 * @param {Object} document
 * @param {string} id
 * @param {Object} values - Partial theme values
 * @param {{ replace?: boolean }} [opts] - replace: each group in values is the
 *   whole group (the editor's full draft), so keys it dropped are removed
 *   instead of surviving a deep merge
 * @returns {Object} The updated document
 */
export function updateTheme(document, id, values, { replace = false } = {}) {
    const normalized = normalizeThemeDocument(document);
    const index = normalized.themes.findIndex((entry) => entry.id === id);
    if (index === -1) throw new Error('Theme not found');

    const current = normalized.themes[index];
    const patch = stripMeta(values || {});
    const next = {
        ...(replace ? { ...current, ...patch } : mergeTheme(current, patch)),
        id: current.id,
        name: values?.name || current.name,
        status: current.status,
        createdAt: current.createdAt || null,
        updatedAt: new Date().toISOString(),
    };

    const themes = [...normalized.themes];
    themes[index] = next;
    return { ...normalized, themes };
}

/**
 * Make one theme the live one. This is the only operation that changes what
 * visitors are served.
 *
 * @param {Object} document
 * @param {string} id
 * @returns {Object} The updated document
 */
export function publishTheme(document, id) {
    const normalized = normalizeThemeDocument(document);
    const target = normalized.themes.find((entry) => entry.id === id);
    if (!target) throw new Error('Theme not found');

    const themes = normalized.themes.map((entry) => {
        if (entry.id === id) return { ...entry, status: THEME_STATUS.LIVE, updatedAt: new Date().toISOString() };
        // The theme being replaced stops being live but is kept so it can be restored.
        if (entry.status === THEME_STATUS.LIVE) return { ...entry, status: THEME_STATUS.DRAFT };
        return entry;
    });

    return { liveId: id, themes };
}

/**
 * @param {Object} document
 * @param {string} id
 * @returns {Object} The updated document
 */
export function deleteTheme(document, id) {
    const normalized = normalizeThemeDocument(document);
    if (normalized.liveId === id) {
        throw new Error('The live theme cannot be deleted. Publish another theme first.');
    }
    if (normalized.themes.length <= 1) {
        throw new Error('The only theme cannot be deleted.');
    }

    return { ...normalized, themes: normalized.themes.filter((entry) => entry.id !== id) };
}

/**
 * Add or remove a theme from the A/B pool.
 *
 * A pooled theme has status 'published': it is served to some visitors alongside
 * the live theme. Drafts are never pooled, and the live theme is always in the
 * pool implicitly, so this only ever promotes drafts.
 *
 * @param {Object} document
 * @param {string} id
 * @param {boolean} pooled
 * @returns {Object} The updated document
 */
export function setThemePooled(document, id, pooled) {
    const normalized = normalizeThemeDocument(document);
    if (normalized.liveId === id) {
        throw new Error('The live theme is always served; it cannot be pooled separately.');
    }

    const themes = normalized.themes.map((entry) => (
        entry.id === id
            ? { ...entry, status: pooled ? THEME_STATUS.PUBLISHED : THEME_STATUS.DRAFT, updatedAt: new Date().toISOString() }
            : entry
    ));

    return { ...normalized, themes };
}

/**
 * Themes a visitor may be served: the live one, plus anything in the A/B pool.
 * Returns just the live theme when no pool has been set up, which is the normal
 * case and keeps the served HTML identical to a single-theme site.
 *
 * @param {Object} document
 * @returns {Array} Theme entries
 */
export function servedThemeEntries(document) {
    const normalized = normalizeThemeDocument(document);
    const live = normalized.themes.find((entry) => entry.id === normalized.liveId);
    if (!live) return [];

    const pooled = normalized.themes.filter(
        (entry) => entry.id !== live.id && entry.status === THEME_STATUS.PUBLISHED,
    );

    return [live, ...pooled];
}

/**
 * @param {Object} document
 * @param {string} id
 * @param {string} name
 * @returns {Object} The updated document
 */
export function renameTheme(document, id, name) {
    const normalized = normalizeThemeDocument(document);
    const themes = normalized.themes.map((entry) => (
        entry.id === id ? { ...entry, name: name || entry.name, updatedAt: new Date().toISOString() } : entry
    ));
    return { ...normalized, themes };
}

// ---------------------------------------------------------------------------
// Dotted-path access, with per-breakpoint override layers
// ---------------------------------------------------------------------------

/**
 * @param {Object} object
 * @param {string} path - e.g. "header.navSize"
 * @returns {*}
 */
export function getPath(object, path) {
    return String(path).split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), object);
}

/**
 * Set a dotted path, returning a new object (no mutation).
 * @param {Object} object
 * @param {string} path
 * @param {*} value
 * @returns {Object}
 */
export function setPath(object, path, value) {
    const keys = String(path).split('.');
    const out = Array.isArray(object) ? [...object] : { ...object };

    let cursor = out;
    keys.slice(0, -1).forEach((key) => {
        const existing = cursor[key];
        cursor[key] = existing && typeof existing === 'object' && !Array.isArray(existing)
            ? { ...existing }
            : {};
        cursor = cursor[key];
    });
    cursor[keys[keys.length - 1]] = value;

    return out;
}

/**
 * The value shown for a path on a given layer: the layer's own override when it
 * has one, otherwise the next larger screen (phone → tablet → desktop).
 *
 * @param {Object} theme
 * @param {string} path
 * @param {string} layer - 'base' | 'phone' | 'tablet'
 * @returns {*}
 */
export function themeValueAtLayer(theme, path, layer = 'base') {
    if (layer === 'phone') {
        const phone = theme?.overrides?.phone?.[path];
        if (phone !== undefined) return phone;
        const tablet = theme?.overrides?.tablet?.[path];
        if (tablet !== undefined) return tablet;
        return getPath(theme, path);
    }
    if (layer !== 'base') {
        const override = theme?.overrides?.[layer]?.[path];
        if (override !== undefined) return override;
    }
    return getPath(theme, path);
}

/**
 * Write a value on a layer. Base writes go to the theme itself, layer writes go
 * into the sparse override map so only what differs is stored.
 *
 * @param {Object} theme
 * @param {string} path
 * @param {*} value
 * @param {string} layer
 * @returns {Object} A new theme
 */
export function setThemeValueAtLayer(theme, path, value, layer = 'base') {
    if (layer === 'base') return setPath(theme, path, value);

    const overrides = { ...(theme.overrides || {}) };
    overrides[layer] = { ...(overrides[layer] || {}), [path]: value };
    return { ...theme, overrides };
}

/**
 * Drop a layer's override for a path, so it inherits the next larger screen again.
 */
export function clearThemeValueAtLayer(theme, path, layer) {
    if (layer === 'base') return theme;

    const overrides = { ...(theme.overrides || {}) };
    const layerMap = { ...(overrides[layer] || {}) };
    delete layerMap[path];
    overrides[layer] = layerMap;
    return { ...theme, overrides };
}

/**
 * @returns {boolean} True when this layer overrides this path
 */
export function isThemeOverridden(theme, path, layer) {
    if (layer === 'base') return false;
    return theme?.overrides?.[layer]?.[path] !== undefined;
}

/**
 * Flatten a layer's overrides onto the theme, for compiling that breakpoint's CSS.
 * Phone also takes tablet's overrides first, so unset phone values follow tablet.
 * @param {Object} theme
 * @param {string} layer
 * @returns {Object} A new theme with the layer applied
 */
export function themeWithOverrides(theme, layer) {
    const base = layer === 'phone' ? themeWithOverrides(theme, 'tablet') : theme;
    const overrides = theme?.overrides?.[layer];
    if (!overrides) return base;

    return Object.entries(overrides).reduce(
        (acc, [path, value]) => setPath(acc, path, value),
        base,
    );
}

export default {
    normalizeThemeDocument,
    findTheme,
    resolveThemeEntry,
    createDraft,
    updateTheme,
    publishTheme,
    deleteTheme,
    renameTheme,
    getPath,
    setPath,
    themeValueAtLayer,
    setThemeValueAtLayer,
    clearThemeValueAtLayer,
    isThemeOverridden,
    themeWithOverrides,
};
